import { SimpleSearchFilter } from 'module/api/filter/types';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { ApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import { isEmpty } from 'lodash';
import { v4 } from 'uuid';
import { isCustomGroupSection } from 'components/organisms/document-editor/extensions/custom-group-section/utils';
import {
  isCustomSectionNode,
  isPeoplePageSourceDerivedQuestionSection,
} from 'components/organisms/document-editor/extensions/custom-section/utils';
import type { SectionData } from './table/types';
import type { TiptapJson, JSONContent } from 'api/page-template/types';

const peoplePageSourceDerivedSectionNodeToSectionData = (
  node: JSONContent,
  filterTransformer: ApiFilterTransformer<GroupedFilter>,
): SectionData | null => {
  if (
    !isCustomSectionNode(node) ||
    !isPeoplePageSourceDerivedQuestionSection(node) ||
    isEmpty(node.attrs.title)
  ) {
    return null;
  }

  return {
    sectionId: node.attrs.templateSectionId ?? v4(),
    sectionName: node.attrs.title,
    filter: node.attrs.filters?.dynamicCondition
      ? filterTransformer.from(node.attrs.filters.dynamicCondition)
      : defaultGroupedFilter(FilterGroupType.User),
  };
};

export const tiptapJsonToSectionData = (
  tiptapJson: TiptapJson,
  filterTransformer: ApiFilterTransformer<GroupedFilter>,
): SectionData[] => {
  return tiptapJson.content
    .map((section) => {
      if (isCustomGroupSection(section) && !isEmpty(section.attrs.title)) {
        return {
          sectionId: section.attrs.templateSectionId ?? v4(),
          sectionName: section.attrs.title,
          filter: section.attrs.filters?.dynamicCondition
            ? filterTransformer.from(section.attrs.filters.dynamicCondition)
            : defaultGroupedFilter(FilterGroupType.User),
          children: section.content
            .map((child) => {
              return peoplePageSourceDerivedSectionNodeToSectionData(
                child,
                filterTransformer,
              );
            })
            .filter((data) => data != null),
        };
      }

      return null;
    })
    .filter((data) => data != null);
};

function removeFilterConditionFromNode(
  node: JSONContent,
  sectionId: string,
): JSONContent {
  if (node.attrs?.templateSectionId === sectionId) {
    return { ...node, attrs: { ...node.attrs, filters: undefined } };
  }
  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.map((child) =>
        removeFilterConditionFromNode(child, sectionId),
      ),
    };
  }

  return node;
}

export function removeFilterConditionFromTiptapJson(
  tiptapJson: TiptapJson,
  section: SectionData,
): TiptapJson {
  return {
    ...tiptapJson,
    content: tiptapJson.content.map((node) =>
      removeFilterConditionFromNode(node, section.sectionId),
    ),
  };
}

function updateFilterConditionInNode(
  node: JSONContent,
  sectionId: string,
  filter: SimpleSearchFilter,
): JSONContent {
  if (node.attrs?.templateSectionId === sectionId) {
    return {
      ...node,
      attrs: { ...node.attrs, filters: { dynamicCondition: filter } },
    };
  }
  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.map((child) =>
        updateFilterConditionInNode(child, sectionId, filter),
      ),
    };
  }

  return node;
}

export function updateFilterConditionInTiptapJson(
  tiptapJson: TiptapJson,
  section: SectionData,
  filter: SimpleSearchFilter,
): TiptapJson {
  return {
    ...tiptapJson,
    content: tiptapJson.content.map((node) =>
      updateFilterConditionInNode(node, section.sectionId, filter),
    ),
  };
}
